import apiClient from './apiClient';

const BASE_URL = '/herramientas/historial-laboral';

/**
 * Envía un PDF de ANSES al backend para parsear la sección "Resumen Historia Laboral".
 * @param {File} file - Archivo PDF
 * @returns {Promise} Resultado del parseo con registros, meses expandidos y solapamientos
 */
export const parsearPdf = async (file) => {
    const formData = new FormData();
    formData.append('archivo', file);

    const response = await apiClient.post(`${BASE_URL}/parsear`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

/**
 * Recalcula la expansión de meses y solapamientos a partir de registros editados.
 * @param {Array} registros - Lista de registros editados
 * @returns {Promise} Resultado recalculado
 */
export const recalcularExpansion = async (registros) => {
    const response = await apiClient.post(`${BASE_URL}/recalcular`, { registros });
    return response.data;
};

/**
 * Confirma la importación de meses como aportes para un cliente.
 * @param {string} cuilCliente - CUIL del cliente
 * @param {Array<string>} meses - Lista de meses en formato YYYY-MM
 * @returns {Promise} Resultado de la importación
 */
export const confirmarImportacion = async (cuilCliente, meses, datosExtra = {}) => {
    const response = await apiClient.post(`${BASE_URL}/confirmar`, {
        cuilCliente,
        meses,
        mesesLey27705: datosExtra.mesesLey27705 ?? null,
        fechaCumpleEdad: datosExtra.fechaCumpleEdad ?? null,
        hijos: datosExtra.hijos ?? null,
    });
    return response.data;
};

/**
 * Guarda el borrador del progreso de revisión de un historial laboral.
 * @param {string} cuil - CUIL extraído del PDF
 * @param {Object} borradorData - Estado completo del progreso en JSON
 */
export const guardarBorrador = async (cuil, borradorData, nombreCompleto) => {
    const response = await apiClient.post(`${BASE_URL}/borrador`, {
        cuil,
        datosJson: JSON.stringify(borradorData),
        nombreCompleto,
    });
    return response.data;
};

/**
 * Obtiene el borrador de un historial laboral por CUIL.
 * @param {string} cuil - CUIL del cliente
 * @returns {Promise} Estado del borrador parseado
 */
export const obtenerBorrador = async (cuil) => {
    const response = await apiClient.get(`${BASE_URL}/borrador/${cuil}`);
    return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Busca borradores por consulta.
 * @param {string} q - Consulta de búsqueda
 * @returns {Promise} Lista de borradores que coinciden con la búsqueda
 */
export const buscarBorrador = async (q) => {
    const response = await apiClient.get(`${BASE_URL}/borrador/buscar`, {
        params: { q },
    });
    return response.data;
};

/**
 * Previsualiza la regularidad RTI sin persistir aportes.
 * @param {Object} payload - Datos para previsualizar regularidad
 * @returns {Promise} Resultado con regularidad y metadatos
 */
export const previsualizarRegularidadRti = async (payload) => {
    const response = await apiClient.post(`${BASE_URL}/regularidad-preview`, payload);
    return response.data;
};

/**
 * Obtiene resumen de aportes actuales de un cliente para confirmar reemplazo total.
 * @param {string} cuilCliente - CUIL del cliente
 * @returns {Promise<{cuilCliente:string, aportesActuales:number}>}
 */
export const obtenerResumenAportesCliente = async (cuilCliente) => {
    const response = await apiClient.get(`${BASE_URL}/cliente/${cuilCliente}/aportes-resumen`);
    return response.data;
};


/**
 * Empleadores aprendidos como pertenecientes a una caja provincial (ej. Córdoba).
 */
export const obtenerCajasAprendidas = async () => {
    const response = await apiClient.get(`${BASE_URL}/cajas-aprendidas`);
    return Array.isArray(response.data) ? response.data : [];
};

/**
 * Aprende (o refuerza) empleadores marcados como de una caja provincial.
 * @param {Array<{empleadorOriginal:string, cuit:string, cajaId:string}>} empleadores
 */
export const aprenderCajasEmpleador = async (empleadores) => {
    const response = await apiClient.post(`${BASE_URL}/cajas-aprendidas`, { empleadores });
    return Array.isArray(response.data) ? response.data : [];
};
